/**
 * Chemistry-check calendar — a month grid over the app's own data (this is the
 * calendar of record in the Resend phase; no external calendars are read).
 *
 * Solid chips = confirmed meetings (scheduled / awaiting completion / complete).
 * Dashed chips = verified overlaps awaiting the PM's Outlook invite.
 * Clicking a chip opens the case drawer. Times render in the viewer's zone.
 */

import { useMemo, useState } from 'react';
import { Button } from './Button';
import type { ChemistryCase } from '../types';

interface CalendarEvent {
  caseId: string;
  caseTag: string;
  clientName: string;
  coachName: string;
  start: Date;
  status: 'confirmed' | 'done' | 'pending';
}

interface Props {
  cases: ChemistryCase[];
  onOpenCase(id: string): void;
}

const CHIP_STYLE: Record<CalendarEvent['status'], React.CSSProperties> = {
  confirmed: {
    background: 'rgba(46,125,50,0.12)',
    color: '#2e7d32',
    border: '1px solid rgba(46,125,50,0.35)',
  },
  done: {
    background: 'rgba(10,21,40,0.06)',
    color: 'var(--mute)',
    border: '1px solid var(--border-1)',
  },
  pending: {
    background: 'rgba(94,53,177,0.08)',
    color: '#5e35b1',
    border: '1px dashed rgba(94,53,177,0.55)',
  },
};

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function chipTime(d: Date): string {
  return d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(' AM', 'a')
    .replace(' PM', 'p')
    .replace(':00', '');
}

export function ChemistryCalendar({ cases, onOpenCase }: Props): JSX.Element {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const today = new Date();

  const events = useMemo<CalendarEvent[]>(() => {
    const out: CalendarEvent[] = [];
    for (const c of cases) {
      for (const t of c.tracks) {
        if (t.scheduledStart) {
          out.push({
            caseId: c.id,
            caseTag: c.tag,
            clientName: c.clientName,
            coachName: t.coachName,
            start: new Date(t.scheduledStart),
            status: t.state === 'complete' ? 'done' : 'confirmed',
          });
        } else if (t.state === 'ready_to_schedule' && t.proposedStart) {
          out.push({
            caseId: c.id,
            caseTag: c.tag,
            clientName: c.clientName,
            coachName: t.coachName,
            start: new Date(t.proposedStart),
            status: 'pending',
          });
        }
      }
    }
    return out.sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [cases]);

  // 6 rows × 7 cols starting on the Sunday on/before the 1st.
  const days = useMemo(() => {
    const first = new Date(month);
    first.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      return d;
    });
  }, [month]);

  const monthLabel = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const shift = (delta: number) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  const inMonth = (d: Date) => d.getMonth() === month.getMonth();

  return (
    <div style={{ background: '#fff', flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border-1)' }}>
        <Button size="sm" variant="ghost" icon="chevronLeft" onClick={() => shift(-1)}>{''}</Button>
        <Button size="sm" variant="ghost" onClick={() => setMonth(startOfMonth(new Date()))}>Today</Button>
        <Button size="sm" variant="ghost" icon="chevronRight" onClick={() => shift(1)}>{''}</Button>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{monthLabel}</span>
        <div style={{ flex: 1 }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--mute)' }}>
          <span style={{ ...CHIP_STYLE.confirmed, width: 10, height: 10, borderRadius: 3, display: 'inline-block' }} />
          Confirmed
          <span style={{ ...CHIP_STYLE.pending, width: 10, height: 10, borderRadius: 3, display: 'inline-block', marginLeft: 8 }} />
          Awaiting invite
          <span style={{ ...CHIP_STYLE.done, width: 10, height: 10, borderRadius: 3, display: 'inline-block', marginLeft: 8 }} />
          Completed
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--border-1)' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="d-meta" style={{ padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: 'minmax(96px, auto)', flex: 1 }}>
        {days.map((d, i) => {
          const dayEvents = events.filter((e) => sameDay(e.start, d));
          const isToday = sameDay(d, today);
          return (
            <div
              key={i}
              style={{
                borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--border-1)',
                borderBottom: i < 35 ? '1px solid var(--border-1)' : 'none',
                padding: 6,
                background: inMonth(d) ? '#fff' : 'rgba(10,21,40,0.02)',
                minWidth: 0,
              }}
            >
              <div
                className="d-tnum"
                style={{
                  fontSize: 12,
                  fontWeight: isToday ? 700 : 400,
                  color: isToday ? '#fff' : inMonth(d) ? 'var(--ink)' : 'var(--mute)',
                  background: isToday ? 'var(--navy)' : 'transparent',
                  borderRadius: 99,
                  width: 22,
                  height: 22,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 4,
                }}
              >
                {d.getDate()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {dayEvents.map((e, j) => (
                  <button
                    key={j}
                    type="button"
                    onClick={() => onOpenCase(e.caseId)}
                    title={`${e.caseTag} · ${e.clientName} × ${e.coachName}${e.status === 'pending' ? ' — awaiting the Outlook invite' : ''}`}
                    style={{
                      ...CHIP_STYLE[e.status],
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: 11,
                      lineHeight: 1.35,
                      padding: '2px 6px',
                      borderRadius: 4,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span className="d-tnum" style={{ fontWeight: 600 }}>{chipTime(e.start)}</span>{' '}
                    {e.clientName.split(' ')[0]} × {e.coachName.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="d-meta" style={{ padding: '8px 16px', fontSize: 11.5, borderTop: '1px solid var(--border-1)' }}>
        This calendar is the app's own record — meetings appear when a PM confirms the invite, and
        dashed entries are verified overlaps waiting on that invite. Times shown in your local zone.
      </div>
    </div>
  );
}
